import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, BellRing, LogOut } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageToggle } from "./LanguageToggle";
import { BottomNav } from "./BottomNav";
import { Footer } from "./Footer";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { enablePush } from "../lib/push";

export function AppShell({ children, bottomNav = true }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleBell = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      await enablePush();
      setPushOn(true);
      toast.success(t("notifsEnabled"));
    } catch (e) {
      if (e.message === "denied") toast.error(t("notifsDenied"));
      else if (e.message === "unsupported") toast.error(t("notifsUnsupported"));
      else toast.error(t("notifsDenied"));
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white">
      <div className="pointer-events-none fixed inset-0 grain opacity-40" />
      <header className="sticky top-0 z-40 glass border-b border-cardline px-4 py-3" data-testid="app-header">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <button onClick={() => navigate("/app")} data-testid="header-logo-btn">
            <Logo size={30} />
          </button>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <button
              onClick={handleBell}
              disabled={pushBusy}
              className={`grid h-9 w-9 place-items-center rounded-full border transition-colors ${pushOn ? "border-gold bg-gold/15 text-gold" : "border-cardline bg-ink2/60 text-slate-300 hover:text-gold"}`}
              data-testid="notifications-btn"
              title={t("enableNotifs")}
              aria-label={t("enableNotifs")}
            >
              {pushOn ? <BellRing size={17} /> : <Bell size={17} />}
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="grid h-9 w-9 place-items-center rounded-full border border-cardline bg-ink2/60 text-slate-300 hover:text-gold"
              aria-label="Logout"
            >
              <LogOut size={17} />
            </button>
            <img
              src={user?.avatar}
              alt={user?.name}
              className="h-9 w-9 rounded-full border border-gold/50 object-cover"
              data-testid="header-avatar"
            />
          </div>
        </div>
      </header>
      <main className="relative mx-auto max-w-5xl px-4 pb-10 pt-4">{children}</main>
      <Footer />
      {bottomNav && <div className="h-24" />}
      {bottomNav && <BottomNav />}
    </div>
  );
}
